import {
  createMateGenerationTask,
  createMateSprite,
  createMateSpriteTask,
  readMateGenerationTask,
  readMateSprite,
  updateMateSprite,
  UpdateMateSpriteParams,
} from "@/apis/script-game";
import i18n from "@/locales";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import {
  MateSprite,
  MateSpriteAnimation,
  MateSpriteMode,
  MateSpriteStatus,
  N8nTaskStatus,
} from "@/types/game";
import moment from "moment";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { ref, Ref } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import { MateSpriteDirection, MateSpriteTaskMode } from "@/enums/game";
import { useGlobalModal } from "@/stores/globalModal";
import { Gender, Mate } from "@/types/entity";

const SECOND = 3;

export const useCreateMateTask = (params: {
  mateSprite: Ref<MateSprite>;
  mode: Ref<MateSpriteMode>;
  finish: () => void;
  selectedImage: Ref<MediaUploadFileItem | null>;
  isGenerating?: Ref<boolean>;
  countDownPercent?: Ref<number>;
}) => {
  const isGenerating = params.isGenerating || ref(false);
  const countDownPercent = params.countDownPercent || ref(0);
  const { mateSprite, mode, finish, selectedImage } = params;
  let countDownInterval: NodeJS.Timeout | undefined = undefined;
  let isStop = false;

  const startCountDown = () => {
    stopCountDown();
    const secondAddPercent = 100 / 150;

    if (mateSprite.value.status === MateSpriteStatus.Processing) {
      const duration = moment().diff(
        moment(mateSprite.value.dtCreate),
        "seconds"
      );
      if (duration > 0) countDownPercent.value += secondAddPercent * duration;
    }

    countDownInterval = setInterval(() => {
      countDownPercent.value += secondAddPercent * SECOND;
      if (countDownPercent.value >= 99) {
        countDownPercent.value = 99;
        if (countDownInterval) {
          clearInterval(countDownInterval);
        }
      }
    }, SECOND * 1000);
  };

  const stopCountDown = () => {
    countDownPercent.value = 0;
    if (countDownInterval) {
      clearInterval(countDownInterval);
      countDownInterval = undefined;
    }

    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
  };

  let timer: NodeJS.Timeout | undefined = undefined;
  const pollTaskStatus = async () => {
    try {
      if (isStop) return;
      if (!mateSprite.value.uuid) {
        stopCountDown();
        isGenerating.value = false;
        return;
      }
      const task = await readMateSprite(mateSprite.value!.uuid!);
      if (task.item.status === MateSpriteStatus.Processing) {
        timer = setTimeout(() => {
          pollTaskStatus();
        }, SECOND * 1000);
      } else if (task.item.status === MateSpriteStatus.Completed) {
        isGenerating.value = false;
        mateSprite.value = task.item;
        mode.value = MateSpriteMode.Image;
        showSuccessToast(i18n.global.t("Message.spriteGeneratedSuccess"));
        finish();
      } else if (task.item.status === MateSpriteStatus.Failed) {
        isGenerating.value = false;
        showFailToast(i18n.global.t("Dungeons.sprite.fail"));
      }
    } catch (e) {
      console.log(e);
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        pollTaskStatus();
      }, 10000);
    }
  };

  const mateStore = useMate();
  const userStore = useGlobalUser();

  const startCreateMateTask = async () => {
    isGenerating.value = true;
    isStop = false;

    const _mateSprite = await createMateSprite({
      mateId: mateStore.currentMate!.id! || mateStore.userMate!.id!,
      mode: MateSpriteMode.Image,
      referenceMediaId: selectedImage.value!.mediaId!,
    });

    mateSprite.value.id = _mateSprite.item.id!;
    mateSprite.value.uuid = _mateSprite.item.uuid!;
    mateSprite.value.mateId = _mateSprite.item.mateId!;
    startCountDown();
    userStore.refresh();

    pollTaskStatus();
  };

  const reset = () => {
    isGenerating.value = false;
    countDownPercent.value = 0;
    stopCountDown();
    isStop = true;
  };

  return {
    startCreateMateTask,
    countDownPercent,
    isGenerating,
    reset,
  };
};

export const useSpriteTask = (params: {
  mateSprite: Ref<MateSprite>;
  taskMode: MateSpriteTaskMode;
  finish: (mateSprite: MateSprite) => void;
  isGenerating?: Ref<boolean>;
  countDownPercent?: Ref<number>;
}) => {
  const { mateSprite, taskMode, finish } = params;

  let isStop = false;
  const countDownPercent = params.countDownPercent || ref(0);
  const isGenerating = params.isGenerating || ref(false);
  const modalStore = useGlobalModal();
  const userStore = useGlobalUser();
  let countDownInterval: NodeJS.Timeout | undefined = undefined;

  const startCountDown = (seconds = 75) => {
    stopCountDown();
    const secondAddPercent = 100 / seconds;

    if (mateSprite.value.status === MateSpriteStatus.Processing) {
      const duration = moment().diff(
        moment(mateSprite.value.dtCreate),
        "seconds"
      );
      if (duration > 0) countDownPercent.value += secondAddPercent * duration;
    }

    countDownInterval = setInterval(() => {
      countDownPercent.value += secondAddPercent * SECOND;
      if (countDownPercent.value >= 99) {
        countDownPercent.value = 99;
        if (countDownInterval) {
          clearInterval(countDownInterval);
        }
      }
    }, SECOND * 1000);
  };

  const stopCountDown = () => {
    countDownPercent.value = 0;
    if (countDownInterval) {
      clearInterval(countDownInterval);
      countDownInterval = undefined;
    }

    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
  };

  const createTask = async (params: {
    spriteId: number;
    direction?: MateSpriteDirection;
    frameIndex?: number;
    referenceMediaId?: number;
    imageMediaId?: number;
    prompt?: string;
    method?: "reference" | "pixel" | "text";
  }) => {
    const {
      spriteId,
      direction,
      frameIndex,
      referenceMediaId,
      imageMediaId,
      prompt,
      method,
    } = params;

    let spentAmount = 0;

    if (taskMode === MateSpriteTaskMode.Frame) {
      spentAmount = direction ? 1 : 4;
    } else if (
      taskMode === MateSpriteTaskMode.Portrait ||
      taskMode === MateSpriteTaskMode.Avatar
    ) {
      spentAmount = 2;
    } else if (taskMode === MateSpriteTaskMode.Full) {
      spentAmount = 8;
    }

    if (
      modalStore.shouldOpenPowerNotEnoughModal({
        spentPowerAmount: spentAmount,
        cb: () => createTask(params),
      })
    )
      return;
    const loading = showLoadingToast(i18n.global.t("Message.taskCreating"));
    try {
      isGenerating.value = true;
      isStop = false;
      await createMateSpriteTask({
        spriteId,
        direction,
        frameIndex,
        mode: taskMode,
        referenceMediaId,
        imageMediaId,
        prompt: prompt || undefined,
        method,
      });

      loading.close();
      showSuccessToast(i18n.global.t("Message.taskCreatedSuccess"));

      userStore.refresh();
      startCountDown(direction ? 25 : 75);

      pollTaskStatus2();
    } catch {
      console.log("createMateSpriteTask failed");
      isGenerating.value = false;
      loading.close();
    }
  };

  // 轮询任务状态
  let timer: NodeJS.Timeout | undefined = undefined;
  const pollTaskStatus2 = async () => {
    try {
      if (isStop) return;
      const task = await readMateSprite(mateSprite.value.uuid!);
      mateSprite.value = task.item;
      if (task.item.status === MateSpriteStatus.Processing) {
        timer = setTimeout(() => {
          pollTaskStatus2();
        }, SECOND * 1000);
      } else if (task.item.status === MateSpriteStatus.Completed) {
        reset();
        if (timer) {
          clearTimeout(timer);
          timer = undefined;
        }
        finish(task.item);
      } else if (task.item.status === MateSpriteStatus.Failed) {
        isGenerating.value = false;
        showFailToast(i18n.global.t("Dungeons.sprite.fail"));
      }
    } catch (e) {
      console.log(e);
      timer = setTimeout(() => {
        pollTaskStatus2();
      }, SECOND * 1000);
    }
  };

  const reset = () => {
    isGenerating.value = false;
    countDownPercent.value = 0;
    stopCountDown();
    isStop = true;
  };

  return {
    createTask,
    countDownPercent,
    isGenerating,
    reset,
  };
};

export const useCreateMateGenerationTask = (params: {
  finish?: (result: { mate: Mate; sprite: MateSprite }) => void;
  isGenerating?: Ref<boolean>;
}) => {
  const isGenerating = params.isGenerating || ref(false);
  const { finish } = params;
  let isStop = false;
  let taskId = "";

  let timer: NodeJS.Timeout | undefined = undefined;
  const pollTaskStatus = async () => {
    try {
      if (isStop) return;
      if (!taskId) {
        isGenerating.value = false;
        return;
      }
      const task = await readMateGenerationTask(taskId);
      if (task.data.status === N8nTaskStatus.Processing) {
        timer = setTimeout(() => {
          pollTaskStatus();
        }, SECOND * 1000);
      } else if (task.data.status === N8nTaskStatus.Completed) {
        isGenerating.value = false;
        finish?.(task.data.result);
      } else if (task.data.status === N8nTaskStatus.Failed) {
        isGenerating.value = false;
        showFailToast(i18n.global.t("Dungeons.sprite.fail"));
      }
    } catch (e) {
      console.log(e);
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        pollTaskStatus();
      }, 10000);
    }
  };

  const userStore = useGlobalUser();

  const startCreateMateGenerationTask = async ({
    referenceMediaId,
    prompt,
    gender,
  }: {
    referenceMediaId?: number;
    prompt?: string;
    gender?: Gender;
  }) => {
    isGenerating.value = true;
    isStop = false;

    const res = await createMateGenerationTask({
      referenceMediaId,
      prompt,
      gender,
    });
    taskId = res.data.taskId;

    userStore.refresh();

    pollTaskStatus();
  };

  const reset = () => {
    isGenerating.value = false;
    isStop = true;
  };

  return {
    startCreateMateGenerationTask,
    isGenerating,
    reset,
  };
};

const taskTimerMap: Record<string, NodeJS.Timeout | null> = {};

export const pollMateGenerationTask = async (taskId: string) => {
  try {
    if (taskTimerMap[taskId]) {
      clearTimeout(taskTimerMap[taskId]);
      taskTimerMap[taskId] = null;
    }

    const task = await readMateGenerationTask(taskId);
    if (task.data.status === N8nTaskStatus.Processing) {
      taskTimerMap[taskId] = setTimeout(() => {
        pollMateGenerationTask(taskId);
      }, SECOND * 1000);
    } else if (task.data.status === N8nTaskStatus.Completed) {
      showSuccessToast(i18n.global.t("Message.mateCreatedSuccess"));
      useMate().fetchMateList();
      useGlobalUser().refresh();
    } else if (task.data.status === N8nTaskStatus.Failed) {
      showFailToast(i18n.global.t("Dungeons.sprite.fail"));
      useMate().fetchMateList();
      useGlobalUser().refresh();
    }
  } catch (e) {
    console.log(e);
    taskTimerMap[taskId] = setTimeout(() => {
      pollMateGenerationTask(taskId);
    }, 10000);
  }
};

export const handleMateSpriteTask = async (
  sprite: MateSprite,
  params:
    | "avatarMediaId"
    | "portraitMediaId"
    | "imageMediaId"
    | "frameMediaId"
    | "all",
  task: (sprite: MateSprite) => Promise<void>
) => {
  let needCreate = false;
  if (params === "all") {
    const list: (keyof MateSprite)[] = [
      "avatarMediaId",
      "portraitMediaId",
      "imageMediaId",
      "frameMediaId",
    ];
    for (const param of list) {
      if (sprite[param]) {
        needCreate = true;
      }
    }
  } else if (sprite[params]) {
    needCreate = true;
  }

  const type = {
    avatarMediaId: i18n.global.t(`sprite.avatar`),
    portraitMediaId: i18n.global.t(`sprite.portrait`),
    imageMediaId: i18n.global.t(`sprite.image`),
    frameMediaId: i18n.global.t(`sprite.frame`),
    all: i18n.global.t(`sprite.allImage`),
  }[params];

  if (needCreate) {
    useGlobalModal().openCustomGlobalActionSheet({
      title: i18n.global.t(`sprite.confirmGenerate`, { type }),
      list: [
        {
          content: i18n.global.t(`sprite.confirmOverwrite`),
          callback: () => {
            // 执行任务
            task(sprite);
          },
        },
        {
          content: i18n.global.t(`sprite.createNewAndContinue`),
          callback: async () => {
            const loading = showLoadingToast(
              i18n.global.t(`sprite.newImageCreation`)
            );
            const newSprite = await cloneMateSprite(sprite);
            loading.close();
            task(newSprite);
          },
        },
      ],
    });
  } else return task(sprite);
};

export const cloneMateSprite = async (sprite: MateSprite) => {
  const res = await createMateSprite({
    mateId: sprite.mateId,
  });

  let newSprite = res.item;

  const animationMap: Record<number, MateSpriteAnimation> = {};
  sprite.animationzz?.forEach((v) => {
    animationMap[v.direction] = v;
  });

  const params: UpdateMateSpriteParams = {
    name: sprite.name,
    mode: sprite.mode,
    referenceMediaId: sprite.referenceMediaId,
    imageMediaId: sprite.imageMediaId,
    frameMediaId: sprite.frameMediaId,
    portraitMediaId: sprite.portraitMediaId,
    avatarMediaId: sprite.avatarMediaId,
  };

  if (newSprite.animationzz?.every((v) => v.mediaId !== 0)) {
    params.animationzz = newSprite.animationzz?.map((v) => ({
      id: v.id!,
      mediaId: animationMap[v.direction]?.mediaId,
    }));
  }

  await updateMateSprite(newSprite.id!, params);

  newSprite = (await readMateSprite(newSprite.uuid!)).item;

  return newSprite;
};
